import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"

import { useAppDispatch } from "@/core/app/hooks"
import { setAlert } from "@/core/app/slices/alert/alertSlice"
import type { AppDispatch } from "@/core/app/store"

import type { ScannerFileEntry } from "./integrations.type"
import { downloadScannerFile, loadScannerFiles } from "./integrationsThunkApi"

// Set to false to suppress all terminal failure toasts from this hook.
const SCANNER_IMPORT_TOAST_ENABLED = true

export interface FailedDownloadEntry {
  entry: ScannerFileEntry
  role: "upper" | "lower" | "photo"
}

export interface RetryWithRefreshOptions {
  externalCaseId: string | number
  scannerName: string
  // Shared with caller — tracks IDs already downloaded to prevent re-queuing the same file.
  downloadedIds: Set<number>
  // Shared with caller — hook appends non-recoverable failures here; caller reads it after executeRetry returns.
  callerFailedDownloads: FailedDownloadEntry[]
  onUpperReady?: (file: File | null) => void
  onLowerReady?: (file: File | null) => void
  onPhotosReady?: (files: File[]) => void | Promise<void>
}

interface BatchResult {
  file: File | null
  role: "upper" | "lower" | "photo"
  entry: ScannerFileEntry
  expired: boolean  // true = 404 response; URL is stale — triggers /load refresh
  aborted: boolean  // true = cancelled by abortAll() after another entry expired
}

// Pre-signed URLs return 404 when expired.
// The interceptor in interceptors.ts rejects with the AxiosResponse (not AxiosError),
// so payload.status is the HTTP status directly — also check .response.status for safety.
function is404Error(payload: unknown): boolean {
  const err = payload as { status?: number; response?: { status?: number } }
  return err?.status === 404 || err?.response?.status === 404
}

// Terminal failure toast — reuses existing i18n keys; no Retry button; stays until route change.
function dispatchTerminalFailureToast(
  dispatch: AppDispatch,
  t: TFunction,
  failed: FailedDownloadEntry[],
): void {
  if (!SCANNER_IMPORT_TOAST_ENABLED) return
  const hasScanFailures = failed.some(
    (f) => f.role === "upper" || f.role === "lower",
  )
  dispatch(
    setAlert({
      message: hasScanFailures
        ? t("scanner.scans.failedToImport")
        : t("scanner.photos.failedToImport"),
      severity: "error",
      timeout: null,  // null = persistent; MUI Snackbar won't auto-dismiss
    }),
  )
}

// Fires upper/lower/photos callbacks for every result that resolved to a File.
async function invokeCallbacks(
  results: BatchResult[],
  options: RetryWithRefreshOptions,
): Promise<void> {
  const photos: File[] = []
  for (const { file, role } of results) {
    if (!(file instanceof File)) continue
    if (role === "upper") options.onUpperReady?.(file)
    else if (role === "lower") options.onLowerReady?.(file)
    else photos.push(file)
  }
  // Photos callback receives all files at once — await so caller can upload before cleanup.
  if (photos.length > 0) await options.onPhotosReady?.(photos)
}

// Classifies a non-fulfilled action as aborted, expired (404), or a plain failure.
function handleDownloadFailure(
  action: ReturnType<
    typeof downloadScannerFile.fulfilled | typeof downloadScannerFile.rejected
  >,
  entry: ScannerFileEntry,
  role: "upper" | "lower" | "photo",
  options: RetryWithRefreshOptions,
  onFirstExpiry: () => void,
): BatchResult {
  // Remove from downloadedIds so the entry can be re-queued (refresh or next retry).
  options.downloadedIds.delete(entry.attachment_id)

  // Aborted by our own abortAll() — not a real failure; will be re-tried with fresh URL.
  if (downloadScannerFile.rejected.match(action) && action.meta.aborted) {
    return { file: null, role, entry, expired: false, aborted: true }
  }

  console.error("Failed to download scanner file:", entry.name)

  if (is404Error(action.payload)) {
    onFirstExpiry()  // sets flag + aborts all other in-flight downloads
    return { file: null, role, entry, expired: true, aborted: false }
  }

  // Non-404 failure — caller will show the standard retry toast for this entry.
  options.callerFailedDownloads.push({ entry, role })
  return { file: null, role, entry, expired: false, aborted: false }
}

// Dispatches one download thunk; registers its abort fn for use if another entry expires.
function dispatchRetryDownload(
  entry: ScannerFileEntry,
  role: "upper" | "lower" | "photo",
  options: RetryWithRefreshOptions,
  dispatch: AppDispatch,
  abortFns: Array<() => void>,
  onFirstExpiry: () => void,
): Promise<BatchResult> {
  if (!entry.download_url || options.downloadedIds.has(entry.attachment_id)) {
    return Promise.resolve({ file: null, role, entry, expired: false, aborted: false })
  }
  options.downloadedIds.add(entry.attachment_id)

  // RTK thunk promise exposes .abort() — stores it so abortAll() can cancel this request.
  const thunkPromise = dispatch(
    downloadScannerFile({
      url: entry.download_url,
      fileName: entry.name,
      scannerName: options.scannerName,
    }),
  )
  abortFns.push(() => thunkPromise.abort())

  return thunkPromise.then((action): BatchResult => {
    if (downloadScannerFile.fulfilled.match(action)) {
      return { file: action.payload, role, entry, expired: false, aborted: false }
    }
    return handleDownloadFailure(action, entry, role, options, onFirstExpiry)
  })
}

// Phase 1: fires all downloads in parallel; first 404 triggers abortAll() for the rest.
function fireDownloadBatch(
  toRetry: FailedDownloadEntry[],
  options: RetryWithRefreshOptions,
  dispatch: AppDispatch,
): Promise<BatchResult[]> {
  const abortFns: Array<() => void> = []
  let urlExpiredDetected = false

  // Guard ensures abortAll() runs only once even if multiple 404s arrive concurrently.
  const onFirstExpiry = () => {
    if (urlExpiredDetected) return
    urlExpiredDetected = true
    abortFns.forEach((fn) => fn())
  }

  return Promise.all(
    toRetry.map(({ entry, role }) =>
      dispatchRetryDownload(entry, role, options, dispatch, abortFns, onFirstExpiry),
    ),
  )
}

// Calls /load once; returns attachment_id → fresh ScannerFileEntry with updated download_url.
async function buildFreshEntryMap(
  externalCaseId: string | number,
  scannerName: string,
  dispatch: AppDispatch,
): Promise<Map<number, ScannerFileEntry>> {
  const result = await dispatch(
    loadScannerFiles({ externalCaseId, scannerName }),
  ).unwrap()
  const { upper, lower, photos } = result.files
  const map = new Map<number, ScannerFileEntry>()
  if (upper) map.set(upper.attachment_id, upper)
  if (lower) map.set(lower.attachment_id, lower)
  photos.forEach((p) => map.set(p.attachment_id, p))
  return map
}

// Phase 2: re-downloads the expired/aborted entries using fresh URLs from /load.
async function redownloadWithFreshUrls(
  toRefresh: FailedDownloadEntry[],
  freshById: Map<number, ScannerFileEntry>,
  options: RetryWithRefreshOptions,
  dispatch: AppDispatch,
): Promise<BatchResult[]> {
  return Promise.all(
    toRefresh.map(async ({ entry, role }) => {
      const fresh = freshById.get(entry.attachment_id)
      // File absent in fresh response or already downloaded — mark as failure.
      if (!fresh?.download_url || options.downloadedIds.has(fresh.attachment_id)) {
        options.callerFailedDownloads.push({ entry, role })
        return { file: null, role, entry, expired: false, aborted: false }
      }
      options.downloadedIds.add(fresh.attachment_id)
      const action = await dispatch(
        downloadScannerFile({
          url: fresh.download_url,
          fileName: fresh.name,
          scannerName: options.scannerName,
        }),
      )
      if (downloadScannerFile.fulfilled.match(action)) {
        return { file: action.payload, role, entry: fresh, expired: false, aborted: false }
      }
      // Second attempt failed — no further retry; terminal toast shown by executeRetry.
      options.downloadedIds.delete(fresh.attachment_id)
      options.callerFailedDownloads.push({ entry: fresh, role })
      return { file: null, role, entry: fresh, expired: false, aborted: false }
    }),
  )
}

// --- Exported hook ---

export function useRetryWithUrlRefresh() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation("common")

  const executeRetry = useCallback(
    async (
      toRetry: FailedDownloadEntry[],
      options: RetryWithRefreshOptions,
    ): Promise<{ terminal: boolean }> => {
      // Phase 1: fire all downloads; first 404 aborts the rest
      const results = await fireDownloadBatch(toRetry, options, dispatch)
      // Call callbacks for files that succeeded (not expired/aborted)
      await invokeCallbacks(
        results.filter((r) => !r.expired && !r.aborted),
        options,
      )

      const urlExpiredDetected = results.some((r) => r.expired)
      if (!urlExpiredDetected) return { terminal: false }

      // Phase 2: call /load once for fresh URLs; re-download only expired + aborted entries
      const toRefresh = results
        .filter((r) => r.expired || r.aborted)
        .map(({ entry, role }) => ({ entry, role }))
      console.log(
        `Scanner URLs expired — refreshing ${toRefresh.length} file(s):`,
        toRefresh.map((f) => f.entry.name).join(", "),
      )

      let freshById: Map<number, ScannerFileEntry>
      try {
        freshById = await buildFreshEntryMap(
          options.externalCaseId,
          options.scannerName,
          dispatch,
        )
      } catch {
        // /load itself failed — cannot recover; show terminal toast
        dispatchTerminalFailureToast(dispatch, t, toRefresh)
        return { terminal: true }
      }

      const refreshResults = await redownloadWithFreshUrls(toRefresh, freshById, options, dispatch)
      await invokeCallbacks(refreshResults, options)

      // Check if any entry from the refresh batch still failed after getting fresh URLs
      const hasFreshDownloadFailures = toRefresh.some((r) =>
        options.callerFailedDownloads.some(
          (f) => f.entry.attachment_id === r.entry.attachment_id,
        ),
      )
      if (hasFreshDownloadFailures) {
        // Drain failures so caller doesn't also show a retry toast on top of the terminal one
        const failed = options.callerFailedDownloads.splice(0)
        dispatchTerminalFailureToast(dispatch, t, failed)
        return { terminal: true }
      }

      return { terminal: false }
    },
    [dispatch, t],
  )

  return { executeRetry }
}
